import React, { useState, useEffect } from "react";
// import BackToServicesButton from '../components/BackToServicesButton';
import API_BASE_URL from '../api';
import { authFetch } from '../authFetch';
import useSelectedRadio from '../hooks/useSelectedRadio';
import { useTranslation } from 'react-i18next';
import { fmtNumber, useLocale } from '../i18n/format';
import { exportPdfMake } from '../pdf/core/pdfService';
import { buildTop40Doc } from '../pdf/builders/top40/buildDoc';
import { exportXlsx } from '../excel/core/excelService';
import { buildTop40Workbook } from '../excel/builders/top40/buildWorkbook';

export default function Top40ServicePage() {
  const [yearMonth, setYearMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [top40, setTop40] = useState([]);
  const [info, setInfo] = useState("");
  const [missingDates, setMissingDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(40);

  const { radioId, shortcode, name: stationName } = useSelectedRadio();
  const { t } = useTranslation('common');
  const locale = useLocale();
const [rawInfo, setRawInfo] = useState("");

  const localizeInfo = (raw, missing) => {
  if (Array.isArray(missing) && missing.length) {
    return `${t('top40.no_playlists_prefix')}: ${missing.join(', ')}`;
  }
  if (raw && /полные данные/i.test(raw)) {
    return t('top40.full_data');
  }
  return raw || "";
};

const handleFetch = async () => {
    if (!radioId || !yearMonth) return;
    const [year, month] = yearMonth.split("-");
    setLoading(true);
    setInfo(t('common.loading'));
    setTop40([]);
    setMissingDates([]);
    try {
      const API = (API_BASE_URL || '').replace(/\/+$/, '');
      const url = `${API}/radios/${encodeURIComponent(radioId)}/metrics/top40?year=${year}&month=${parseInt(month, 10)}&limit=${limit}`;
      const resp = await authFetch(url);
      const json = await resp.json();
      if (json.detail || json.error) throw new Error(json.detail || json.error);
      setTop40(json.top40 || []);
      setRawInfo(json.info || "");
      setMissingDates(json.missing_dates || []);
      setInfo(localizeInfo(json.info || "", json.missing_dates || []));
    } catch (e) {
      setInfo(t('top40.load_error'));
      setTop40([]);
      setMissingDates([]);
    }
    setLoading(false);
  };

  const exportTop40 = async (format) => {
    if (!radioId || !yearMonth) return;
    const [year, month] = yearMonth.split("-");
    const numericYear = Number(year);
    const numericMonth = Number(month);
    const monthLabel = new Date(numericYear, numericMonth - 1, 1).toLocaleString(locale, { month: 'long', year: 'numeric' });
    const titleCandidate = t('top40.title_with_limit', { limit });
    const resolvedServiceTitle = titleCandidate && titleCandidate !== 'top40.title_with_limit'
      ? titleCandidate
      : t('top40.title');

    if (format === 'xlsx') {
      try {
        await exportXlsx({
          filename: `top${limit}_${shortcode}_${year}-${String(month).padStart(2, '0')}.xlsx`,
          buildWorkbook: (XLSX) => buildTop40Workbook(XLSX, {
            t,
            shortcode,
            stationName,
            serviceName: resolvedServiceTitle,
            year: numericYear,
            month: numericMonth,
            monthLabel,
            rows: top40,
            info,
            missingDates,
            limit
          })
        });
      } catch (e) {
        const message = e?.message || String(e);
        setInfo(t('top40.export_error', { message }));
        alert(t('top40.export_error', { message }));
      }
      return;
    }

    if (format !== 'pdf') {
      const API = (API_BASE_URL || "").replace(/\/+$/, "");
      const url = `${API}/radios/${encodeURIComponent(radioId)}/metrics/top40/export?year=${year}&month=${parseInt(month, 10)}&limit=${limit}&format=${encodeURIComponent(format)}`;
      try {
        const resp = await fetch(url, { method: "GET", credentials: "include", mode: "cors" });
        if (!resp.ok) {
          const txt = await resp.text().catch(() => "");
          throw new Error(`${resp.status} ${resp.statusText}${txt ? " — " + txt.slice(0, 300) : ""}`);
        }
        const cd = resp.headers.get("Content-Disposition") || "";
        let filename = `top40_${shortcode}_${year}-${String(month).padStart(2, "0")}.${format}`;
        const m = cd.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);

        if (m && m[1]) filename = decodeURIComponent(m[1].replace(/"/g, ""));

        const blob = await resp.blob();
        const href = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = href;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(href);
      } catch (e) {
        const message = e?.message || String(e);
        setInfo(t('top40.export_error', { message }));
        alert(t('top40.export_error', { message }));
      }
      return;
    }

    try {
      const logoUrl = `${process.env.PUBLIC_URL || ''}/Radiomonitor.NetLogo.png`;
      let logoDataUrl = null;
      try {
        const r = await fetch(logoUrl, { cache: 'no-store' });
        const b = await r.blob();
        const reader = new FileReader();
        logoDataUrl = await new Promise((res, rej) => {
          reader.onloadend = () => res(reader.result);
          reader.onerror = rej;
          reader.readAsDataURL(b);
        });
      } catch {}

      const filename = `top40_${shortcode}_${year}-${String(month).padStart(2, '0')}.pdf`;
      const docDefinition = buildTop40Doc({
        t, shortcode, stationName,
        serviceName: resolvedServiceTitle,
        year: numericYear,
        month: numericMonth,
        monthLabel,
        rows: top40,
        info,
        missingDates,
        fmtNumber,
        locale,
        logoDataUrl
      });
      await exportPdfMake(docDefinition, filename);
    } catch (e) {
      const message = e?.message || String(e);
      setInfo(t('top40.export_error', { message }));
      alert(t('top40.export_error', { message }));
    }
  };

  const monthOptions = [];
  useEffect(() => {
    setInfo(localizeInfo(rawInfo, missingDates));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, locale, rawInfo, missingDates]);

  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    monthOptions.push(
      <option key={ym} value={ym}>
        {d.toLocaleString(locale, { month: "long", year: "numeric" })}
      </option>
    );
  }
  const exportDisabled = loading || !top40.length;
  const showButtonStyle = buildPrimaryButtonStyle();
  const exportButtonStyle = buildPrimaryButtonStyle(exportDisabled);

  if (!radioId) {
    return (
      <div style={widgetStyle}>
        <h2 style={{ marginBottom: 14 }}>{t('top40.title')}</h2>
        <div style={{ color: '#e54848', fontWeight: 600, fontSize: 18 }}>
          {t('common.no_channel')}
        </div>
      </div>
    );
  }
  return (
    <div style={widgetStyle}>
      {/* BackToServicesButton removed */}
      <h2 style={{ marginBottom: 14 }}>{t('top40.title')}</h2>
      <div style={filtersRowStyle}>
        <div style={filterPairLeftStyle}>
          <span style={filterLabelLeftStyle}>{t('top40.pick_month')}:</span>
          <select value={yearMonth} onChange={e => setYearMonth(e.target.value)} style={inputStyle}>
            {monthOptions}
          </select>
        </div>
        <div style={filterSpacerStyle} />
        <div style={filterPairRightStyle}>
          <span style={filterLabelRightStyle}>{t('top40.pick_limit')}:</span>
          <select value={limit} onChange={e => setLimit(Number(e.target.value))} style={inputStyle}>
            {[10, 20, 40, 50, 100].map(n => (
              <option key={n} value={n}>TOP-{n}</option>
            ))}
          </select>
        </div>
      </div>
      <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: ACTION_BUTTON_GAP, flexWrap: "wrap" }}>
        <button onClick={handleFetch} style={showButtonStyle}>{t('top40.build_button')}</button>
        <div style={{ display: 'inline-flex', gap: ACTION_BUTTON_GAP }}>
          <button
            onClick={() => exportTop40("pdf")}
            type="button"
            style={exportButtonStyle}
            disabled={exportDisabled}
          >
            {t('top40.export_pdf')}
          </button>
          <button
            onClick={() => exportTop40("xlsx")}
            type="button"
            style={exportButtonStyle}
            disabled={exportDisabled}
          >
            {t('top40.export_excel')}
          </button>
        </div>
      </div>
      <div style={{ margin: "10px 0", fontWeight: 500 }}>
        {info}
        {missingDates.length > 0 && (
          <div style={{ color: "red", marginTop: 8, fontSize: 15 }}>
            {t('top40.missing_dates')}: {missingDates.join(", ")}
          </div>
        )}
      </div>
      <table style={{ minWidth: 320, width: "100%", display: top40.length ? "" : "none", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "#f7f7fa" }}>
            <th style={thTd}>{t('top40.table.rank')}</th>
            <th style={thTd}>{t('top40.table.artist')}</th>
            <th style={thTd}>{t('top40.table.track')}</th>
            <th style={thTd}>{t('top40.table.count')}</th>
          </tr>
        </thead>
        <tbody>
          {top40.map((t, i) => (
            <tr key={i}>
              <td style={thTd}>{i + 1}</td>
              <td style={thTd}>{t.artist}</td>
              <td style={thTd}>{t.track}</td>
              <td style={thTd}>{fmtNumber(t.count, locale)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!loading && top40.length === 0 && (
        <div style={{ margin: "14px 0" }}>{t('no_data')}</div>
      )}
    </div>
  );
}

const widgetStyle = {
  maxWidth: 900,
  margin: "40px auto",
  background: "#fff",
  borderRadius: 14,
  padding: 24,
  boxShadow: "0 2px 12px 0 #0001"
};

const inputStyle = {
  padding: 7,
  borderRadius: 4,
  border: "1px solid #ccc",
  fontSize: 16,
  minWidth: 160
};

const ACTION_BUTTON_WIDTH = 216;
const ACTION_BUTTON_GAP = 12;
const FILTER_FIELD_WIDTH = 320;
const FILTER_SPACER_WIDTH = 32;
const FILTER_LABEL_WIDTH = 200;
const FILTER_LABEL_FIELD_GAP = 5;

const actionButtonBase = {
  border: "none",
  padding: "8px 16px",
  borderRadius: 5,
  fontWeight: 600,
  minWidth: ACTION_BUTTON_WIDTH,
  textAlign: "center"
};

const filtersRowStyle = {
  marginBottom: 12,
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap"
};

const filterPairBaseStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: FILTER_LABEL_FIELD_GAP,
  width: FILTER_FIELD_WIDTH,
  minWidth: FILTER_FIELD_WIDTH
};

const filterPairLeftStyle = {
  ...filterPairBaseStyle,
  justifyContent: "flex-start",
  textAlign: "left"
};

const filterPairRightStyle = {
  ...filterPairBaseStyle,
  justifyContent: "flex-end",
  textAlign: "right"
};

const filterLabelBaseStyle = {
  display: "inline-block",
  whiteSpace: "nowrap"
};

const filterLabelLeftStyle = {
  ...filterLabelBaseStyle,
  textAlign: "left"
};

const filterLabelRightStyle = {
  ...filterLabelBaseStyle,
  width: FILTER_LABEL_WIDTH,
  minWidth: FILTER_LABEL_WIDTH,
  textAlign: "right"
};

const filterSpacerStyle = {
  width: FILTER_SPACER_WIDTH,
  minWidth: FILTER_SPACER_WIDTH
};

function buildPrimaryButtonStyle(disabled = false) {
  return {
    ...actionButtonBase,
    background: disabled ? "#9bbdfd" : "#3867fa",
    color: "#fff",
    cursor: disabled ? "not-allowed" : "pointer"
  };
}

const thTd = {
  border: "1px solid #e0e0e0",
  padding: "7px 10px",
  textAlign: "left",
  verticalAlign: "top"
};
